import { baseApi } from "../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../core/data/rtk/apiResponseType";

// Define paginated data interface to match the actual API response structure
export interface InvoiceData {
  items: any[]; // Using any for flexibility with the response structure
  currentPage: number; // Changed from current_page to match other components
  pageSize: number; // Changed from per_page to match other components
  totalItems: number; // Changed from total to match other components
  totalPages: number; // Added missing field from other components
}

// Interface for generate invoices response
export interface GenerateInvoicesResponse {
  generated: number;
  message: string;
}

export const invoiceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllInvoices: builder.query<
      ApiResponseType<InvoiceData>,
      {
        size?: number;
        search?: string;
        page?: number;
        center_id?: number;
        course_id?: number;
        status?: string;
        fee_type?: string;
        from_date?: string;
        to_date?: string;
      }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.size) queryParams.append("size", params.size.toString());
        if (params?.search) queryParams.append("search", params.search);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.center_id)
          queryParams.append("center_id", params.center_id.toString());
        if (params?.course_id)
          queryParams.append("course_id", params.course_id.toString());
        if (params?.status) queryParams.append("status", params.status);
        if (params?.fee_type) queryParams.append("fee_type", params.fee_type);
        if (params?.from_date)
          queryParams.append("from_date", params.from_date);
        if (params?.to_date) queryParams.append("to_date", params.to_date);

        return `invoices/get-all?${queryParams.toString()}`;
      },
      providesTags: ["Invoices"],
    }),

    // Endpoint to generate invoices for a specific center
    generateInvoicesForCenter: builder.mutation<
      ApiResponseType<{ generated: number; message: string }>,
      { center_id: string | number }
    >({
      query: ({ center_id }) => ({
        url: `invoices/course-fee/generate/${center_id}`,
        method: "POST",
      }),
      invalidatesTags: ["Invoices"],
    }),

    // New endpoint for generating registration fee invoices
    generateRegistrationInvoicesForCenter: builder.mutation<
      ApiResponseType<{ generated: number; message: string }>,
      { center_id: string | number }
    >({
      query: ({ center_id }) => ({
        url: `invoices/course-registration-fee/generate/${center_id}`,
        method: "POST",
      }),
      invalidatesTags: ["Invoices"],
    }),

    // Add the deleteInvoice endpoint
    deleteInvoice: builder.mutation<
      ApiResponseType<{ deleted: boolean }>,
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `invoices/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoices"],
    }),
  }),
});

export const {
  useGetAllInvoicesQuery,
  useGenerateInvoicesForCenterMutation,
  useGenerateRegistrationInvoicesForCenterMutation,
  useDeleteInvoiceMutation,
} = invoiceApi;
